import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Paper,
  InputAdornment,
} from '@mui/material';
import { motion } from 'framer-motion';
import { 
  Settings as SettingsIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { chickenRoadAPI } from '../../../services/api';

const ChickenRoadSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await chickenRoadAPI.getSettings();
      if (response.data.success) {
        setSettings(response.data.settings);
        setFormData(response.data.settings);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to fetch settings');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      const response = await chickenRoadAPI.updateSettings(formData);
      if (response.data.success) {
        toast.success('Settings updated successfully');
        setSettings(response.data.settings);
        setFormData(response.data.settings);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (status) => {
    try {
      const response = await chickenRoadAPI.toggleGameStatus({ status });
      if (response.data.success) {
        toast.success(`Game status changed to ${status}`);
        fetchSettings();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to toggle status');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            🐔 Chicken Road Settings
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure game parameters, timing, and multiplier settings
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchSettings}
        >
          Refresh
        </Button>
      </Box>

      {/* Game Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">Game Status</Typography>
            <Chip
              label={settings?.status?.toUpperCase()}
              color={
                settings?.status === 'active' ? 'success' :
                settings?.status === 'maintenance' ? 'warning' : 'error'
              }
              icon={settings?.status === 'active' ? <CheckCircleIcon /> : <WarningIcon />}
            />
          </Box>
          
          {settings?.status !== 'active' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Game is currently in {settings?.status} mode. Players cannot place bets.
            </Alert>
          )}

          <Box display="flex" gap={2}>
            <Button
              variant={settings?.status === 'active' ? 'contained' : 'outlined'}
              color="success"
              onClick={() => handleToggleStatus('active')}
              disabled={settings?.status === 'active'}
            >
              Active
            </Button>
            <Button
              variant={settings?.status === 'maintenance' ? 'contained' : 'outlined'}
              color="warning"
              onClick={() => handleToggleStatus('maintenance')}
              disabled={settings?.status === 'maintenance'}
            >
              Maintenance
            </Button>
            <Button
              variant={settings?.status === 'disabled' ? 'contained' : 'outlined'}
              color="error"
              onClick={() => handleToggleStatus('disabled')}
              disabled={settings?.status === 'disabled'}
            >
              Disabled
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        {/* Timing Configuration */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                ⏱️ Timing Configuration
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <TextField
                fullWidth
                label="Lobby Duration (ms)"
                type="number"
                value={formData.lobby_duration_ms || ''}
                onChange={(e) => handleInputChange('lobby_duration_ms', parseInt(e.target.value))}
                margin="normal"
                helperText="Betting window duration (default: 8000ms = 8 seconds)"
                InputProps={{
                  endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                }}
              />
              
              <TextField
                fullWidth
                label="Max Round Duration (ms)"
                type="number"
                value={formData.max_round_duration_ms || ''}
                onChange={(e) => handleInputChange('max_round_duration_ms', parseInt(e.target.value))}
                margin="normal"
                helperText="Maximum time a round can last (default: 60000ms = 60s)"
                InputProps={{
                  endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                }}
              />
              
              <TextField
                fullWidth
                label="Settling Duration (ms)"
                type="number"
                value={formData.settling_duration_ms || ''}
                onChange={(e) => handleInputChange('settling_duration_ms', parseInt(e.target.value))}
                margin="normal"
                helperText="Pause between rounds (default: 3000ms = 3s)"
                InputProps={{
                  endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Bet Limits */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                💰 Bet Limits
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <TextField
                fullWidth
                label="Minimum Bet Amount"
                type="number"
                value={formData.min_bet_amount || ''}
                onChange={(e) => handleInputChange('min_bet_amount', parseFloat(e.target.value))}
                margin="normal"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
              
              <TextField
                fullWidth
                label="Maximum Bet Amount"
                type="number"
                value={formData.max_bet_amount || ''}
                onChange={(e) => handleInputChange('max_bet_amount', parseFloat(e.target.value))}
                margin="normal"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
              />
              
              <TextField
                fullWidth
                label="Maximum Payout Amount"
                type="number"
                value={formData.max_payout_amount || ''}
                onChange={(e) => handleInputChange('max_payout_amount', parseFloat(e.target.value))}
                margin="normal"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                helperText="Maximum single payout limit"
              />
              
              <TextField
                fullWidth
                label="Max Auto Cashout Multiplier"
                type="number"
                value={formData.max_auto_cashout || ''}
                onChange={(e) => handleInputChange('max_auto_cashout', parseFloat(e.target.value))}
                margin="normal"
                InputProps={{
                  endAdornment: <InputAdornment position="end">x</InputAdornment>,
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Multiplier Configuration */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📊 Multiplier Configuration
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <TextField
                fullWidth
                label="Min Crash Point"
                type="number"
                value={formData.min_crash_point || ''}
                onChange={(e) => handleInputChange('min_crash_point', parseFloat(e.target.value))}
                margin="normal"
                InputProps={{
                  endAdornment: <InputAdornment position="end">x</InputAdornment>,
                }}
              />
              
              <TextField
                fullWidth
                label="Max Crash Point"
                type="number"
                value={formData.max_crash_point || ''}
                onChange={(e) => handleInputChange('max_crash_point', parseFloat(e.target.value))}
                margin="normal"
                InputProps={{
                  endAdornment: <InputAdornment position="end">x</InputAdornment>,
                }}
              />
              
              <FormControl fullWidth margin="normal">
                <InputLabel>Growth Curve Type</InputLabel>
                <Select
                  value={formData.growth_curve_type || 'EXPONENTIAL'}
                  label="Growth Curve Type"
                  onChange={(e) => handleInputChange('growth_curve_type', e.target.value)}
                >
                  <MenuItem value="LINEAR">Linear</MenuItem>
                  <MenuItem value="EXPONENTIAL">Exponential (Default)</MenuItem>
                  <MenuItem value="LOGARITHMIC">Logarithmic</MenuItem>
                  <MenuItem value="SMOOTH">Smooth</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                label="Animation Speed Factor"
                type="number"
                value={formData.animation_speed_factor || ''}
                onChange={(e) => handleInputChange('animation_speed_factor', parseFloat(e.target.value))}
                margin="normal"
                inputProps={{ step: 0.1, min: 0.5, max: 2.0 }}
                helperText="Multiplier growth speed (0.5 = slow, 2.0 = fast)"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* House Edge Configuration */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🏠 House Edge & RTP
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <TextField
                fullWidth
                label="House Edge Percentage"
                type="number"
                value={formData.house_edge_percentage || ''}
                onChange={(e) => handleInputChange('house_edge_percentage', parseFloat(e.target.value))}
                margin="normal"
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                helperText="House profit percentage (default: 3%)"
              />
              
              <TextField
                fullWidth
                label="Target RTP Percentage"
                type="number"
                value={formData.target_rtp_percentage || ''}
                onChange={(e) => handleInputChange('target_rtp_percentage', parseFloat(e.target.value))}
                margin="normal"
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                helperText="Return to player target (default: 97%)"
              />
              
              <TextField
                fullWidth
                label="Force 1.00x Crash Probability"
                type="number"
                value={formData.force_crash_at_1x_probability || ''}
                onChange={(e) => handleInputChange('force_crash_at_1x_probability', parseFloat(e.target.value))}
                margin="normal"
                inputProps={{ step: 0.0001, min: 0, max: 0.1 }}
                helperText="Instant crash probability (default: 0.01 = 1%)"
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Risk Management */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                🛡️ Risk Management
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <TextField
                fullWidth
                label="Max Concurrent Bets Per User"
                type="number"
                value={formData.max_concurrent_bets_per_user || ''}
                onChange={(e) => handleInputChange('max_concurrent_bets_per_user', parseInt(e.target.value))}
                margin="normal"
                helperText="Maximum bets a user can place in one round"
              />
              
              <TextField
                fullWidth
                label="Max Total Exposure Per Round"
                type="number"
                value={formData.max_total_exposure_per_round || ''}
                onChange={(e) => handleInputChange('max_total_exposure_per_round', parseFloat(e.target.value))}
                margin="normal"
                InputProps={{
                  startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                }}
                helperText="Leave empty for unlimited exposure"
              />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enable_latency_protection || false}
                    onChange={(e) => handleInputChange('enable_latency_protection', e.target.checked)}
                  />
                }
                label="Enable Latency Protection"
                sx={{ mt: 2 }}
              />
              
              <TextField
                fullWidth
                label="Max Allowed Latency (ms)"
                type="number"
                value={formData.max_allowed_latency_ms || ''}
                onChange={(e) => handleInputChange('max_allowed_latency_ms', parseInt(e.target.value))}
                margin="normal"
                disabled={!formData.enable_latency_protection}
                InputProps={{
                  endAdornment: <InputAdornment position="end">ms</InputAdornment>,
                }}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Additional Features */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                ⚙️ Additional Features
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.leaderboard_enabled || false}
                    onChange={(e) => handleInputChange('leaderboard_enabled', e.target.checked)}
                  />
                }
                label="Enable Leaderboard"
                sx={{ mb: 2 }}
              />
              
              <TextField
                fullWidth
                label="Config Change Note"
                multiline
                rows={3}
                value={formData.config_change_note || ''}
                onChange={(e) => handleInputChange('config_change_note', e.target.value)}
                margin="normal"
                placeholder="Add a note about this configuration change..."
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Save Button */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          onClick={fetchSettings}
          disabled={saving}
        >
          Reset
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
          onClick={handleSaveSettings}
          disabled={saving}
          size="large"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
    </motion.div>
  );
};

export default ChickenRoadSettings;
